// One cycle at /portal/health/:cycleId. Header stats plus tabs: Invitations
// (the participation view a facilitator watches while a cycle runs) and
// Analysis (anonymised results once enough responses are in). Admins also get
// a per-cycle Settings tab. The anonymity wall holds throughout: delivery
// status by name, response content only in aggregate, never both together.

import { useCallback, useEffect, useState, type JSX } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { healthApi, HealthApiError, type CycleDetail } from './api';
import { CYCLE_STATUS_LABELS, formatNzDate } from './format';
import { InvitationsTab } from './InvitationsTab';
import { AnalysisTab } from './AnalysisTab';
import { CycleSettingsTab } from './CycleSettingsTab';
import { TrendTab } from './TrendTab';

type Tab = 'invitations' | 'analysis' | 'trend' | 'settings';

export function CyclePage(): JSX.Element {
  const { cycleId } = useParams<{ cycleId: string }>();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<CycleDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<Tab>('invitations');

  const load = useCallback(() => {
    if (!cycleId) return;
    healthApi
      .cycle(cycleId)
      .then((res) => {
        setDetail(res);
        setError(null);
      })
      .catch((err: unknown) => {
        if (err instanceof HealthApiError && err.status === 401) {
          navigate(`/auth/login?returnTo=${encodeURIComponent(`/portal/health/${cycleId}`)}`, {
            replace: true,
          });
          return;
        }
        setError(
          err instanceof HealthApiError && err.status === 404
            ? 'This assessment could not be found, or it is not within your scope.'
            : err instanceof Error
              ? err.message
              : 'Unknown error',
        );
      })
      .finally(() => setLoading(false));
  }, [cycleId, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <p style={{ color: 'var(--health-grey)' }}>Loading...</p>;
  if (error || !detail) {
    return (
      <div className="card" style={{ maxWidth: 560, margin: '30px auto', textAlign: 'center' }}>
        <p style={{ fontSize: 14, color: 'var(--health-grey)', lineHeight: 1.7 }}>
          {error ?? 'This assessment could not be loaded.'}
        </p>
      </div>
    );
  }

  const { cycle, participation, is_admin: isAdmin } = detail;
  const completion =
    participation.invited > 0
      ? Math.round((participation.submitted / participation.invited) * 100)
      : null;

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
          <h1 style={{ fontSize: 22 }}>{cycle.church_name}</h1>
          <span className={cycle.status === 'open' ? 'pill pill-success' : 'pill pill-info'}>
            {CYCLE_STATUS_LABELS[cycle.status] ?? cycle.status}
          </span>
        </div>
        <p style={{ fontSize: 13.5, color: 'var(--health-grey)', marginTop: 4 }}>
          {cycle.facilitator_name ? `Facilitated by ${cycle.facilitator_name}. ` : ''}
          {cycle.closes_at
            ? cycle.status === 'open'
              ? `Closes ${formatNzDate(cycle.closes_at)}.`
              : `Closed ${formatNzDate(cycle.closes_at)}.`
            : ''}
        </p>
      </div>

      {/* Stats */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
          gap: 12,
          marginBottom: 18,
        }}
      >
        <Stat label="Invited" value={String(participation.invited)} />
        <Stat label="Opened" value={String(participation.opened)} />
        <Stat label="Submitted" value={String(participation.submitted)} />
        <Stat label="Completion" value={completion === null ? 'n/a' : `${completion}%`} />
        <Stat
          label="Results"
          value={participation.analysis_unlocked ? 'Ready to view' : 'Gathering responses'}
          small
        />
      </div>

      {/* Tabs */}
      <div
        role="tablist"
        aria-label="Cycle views"
        style={{ display: 'flex', gap: 4, borderBottom: '1.5px solid #e5e0d5', marginBottom: 18 }}
      >
        <TabButton id="invitations" current={tab} onSelect={setTab}>
          Invitations
        </TabButton>
        <TabButton id="analysis" current={tab} onSelect={setTab}>
          Analysis
        </TabButton>
        <TabButton id="trend" current={tab} onSelect={setTab}>
          Trend
        </TabButton>
        {isAdmin && (
          <TabButton id="settings" current={tab} onSelect={setTab}>
            Cycle settings
          </TabButton>
        )}
      </div>

      {tab === 'invitations' && <InvitationsTab detail={detail} reload={load} />}
      {tab === 'analysis' && <AnalysisTab detail={detail} />}
      {tab === 'trend' && <TrendTab detail={detail} />}
      {tab === 'settings' && isAdmin && <CycleSettingsTab detail={detail} reload={load} />}
    </div>
  );
}

function Stat({
  label,
  value,
  small,
}: {
  label: string;
  value: string;
  small?: boolean;
}): JSX.Element {
  return (
    <div className="card" style={{ padding: '12px 16px' }}>
      <p
        style={{
          fontSize: 11,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: 'var(--health-grey)',
          marginBottom: 2,
        }}
      >
        {label}
      </p>
      <p style={{ fontSize: small ? 14 : 20, fontWeight: 700 }}>{value}</p>
    </div>
  );
}

function TabButton({
  id,
  current,
  onSelect,
  children,
}: {
  id: Tab;
  current: Tab;
  onSelect: (tab: Tab) => void;
  children: React.ReactNode;
}): JSX.Element {
  const active = id === current;
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={() => onSelect(id)}
      style={{
        background: 'none',
        border: 'none',
        borderBottom: active ? '2.5px solid var(--health-accent)' : '2.5px solid transparent',
        marginBottom: -1.5,
        padding: '10px 16px',
        font: 'inherit',
        fontSize: 14,
        fontWeight: active ? 700 : 500,
        color: active ? 'var(--health-accent)' : 'var(--health-grey)',
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}
